import { ready } from "./Ready.js";

interface ComponentRegistration {
    selector: string;
    callback: (element: HTMLElement) => void;
    hash: string;
    once: boolean;
}

interface ComponentRegistrationValue {
    registration: ComponentRegistration;
    dispose(): void;
}

class ComponentHandler {
    private _loaded: boolean = false;
    private _hashCounter: number = 0;
    private _components: ComponentRegistration[] = [];

    constructor() {
        ready(() => {
            this.updateAll();
            this.handleDomChanges();
            this._loaded = true;
        });
    }

    private handleDomChanges(): void {
        if (!("MutationObserver" in window)) {
            /**
             * Fallback zu "Polling", wenn MutationObserver nicht unterstützt wird.
             */
            setInterval(() => {
                this.updateAll();
            }, 100);
            return;
        }

        let queuedTask = false;
        const observer = new MutationObserver((mutations) => {
            if (queuedTask) {
                return;
            }
            queuedTask = true;
            queueMicrotask(() => {
                queuedTask = false;
                this.updateAll();
            });
        });
        observer.observe(document, {
            subtree: true,
            childList: true,
            attributes: false,
            characterData: false,
        });
    }

    public register<T extends HTMLElement = HTMLElement>(options: {
        selector: string;
        callback: (element: T) => void;
        once?: boolean;
    }): ComponentRegistrationValue {
        const hash = "hash" + this._hashCounter++;

        const { selector, callback, once = false } = options;

        const selectors: string[] = selector.split(",");
        for (let i = 0; i < selectors.length; ++i) {
            selectors[i] += `:not([data-inithash~="${hash}"])`;
        }

        const comp: ComponentRegistration = {
            selector: selectors.join(","),
            callback: callback as any,
            once,
            hash,
        };

        this._components.push(comp);

        if (this._loaded) {
            this.update(comp);
        }

        return {
            registration: comp,
            dispose: () => {
                const index = this._components.indexOf(comp);
                if (index >= 0) {
                    this._components.splice(index, 1);
                }
            },
        };
    }

    private update(component: ComponentRegistration): number {
        let count = 0;
        document.querySelectorAll(component.selector).forEach((element) => {
            if (element.hasAttribute("data-inithash")) {
                let attr = element.getAttribute("data-inithash");
                if (attr.indexOf(component.hash) === -1) {
                    attr += " " + component.hash;
                    element.setAttribute("data-inithash", attr);
                } else {
                    return;
                }
            } else {
                element.setAttribute("data-inithash", component.hash);
            }
            component.callback(element as HTMLElement);
            count++;
        });

        if (count > 0 && component.once) {
            const index = this._components.indexOf(component);
            if (index >= 0) {
                this._components.splice(index, 1);
            }
        }
        return count;
    }

    private updateAll(): number {
        let count = 0;
        const components = this._components.slice();
        for (const component of components) {
            count += this.update(component);
        }
        return count;
    }
}

export const component = new ComponentHandler();
